/**
 * Bug 3 — "candidate closed the tab/browser" beacon.
 *
 * Fires navigator.sendBeacon('/api/session/closed', { slug, reason }) so the
 * backend can finalize a deliberately-closed interview promptly (and generate
 * its report) instead of waiting out the full max-pause grace. Best-effort: it
 * does NOT fire on OS kill / crash / hard network loss — the server-side
 * max-pause watchdog is the guaranteed backstop.
 *
 * Fired from BOTH `beforeunload` and `visibilitychange` → hidden, because
 * `beforeunload` is unreliable on mobile / backgrounded tabs. The backend only
 * acts on the signal once the connection is also paused/stale, so a brief
 * tab-switch that fires a visibility beacon but keeps the socket alive is safe.
 */
export const notifySessionClosed = (apiBaseUrl, slug, reason = 'tab_closed') => {
    if (!slug || typeof navigator === 'undefined' || !navigator.sendBeacon)
        return;
    try {
        const url = `${apiBaseUrl}/api/session/closed`;
        // text/plain avoids a CORS preflight (sendBeacon can't set JSON headers
        // anyway); the backend parses the raw body defensively.
        const blob = new Blob([JSON.stringify({ slug, reason })], { type: 'text/plain' });
        navigator.sendBeacon(url, blob);
    }
    catch (_a) {
        // Beacon is best-effort; never throw during unload.
    }
};
/**
 * Register the close-beacon listeners for the lifetime of an active interview.
 * Returns a cleanup function that removes them. `getSlug` is read lazily so the
 * slug can be resolved after the listeners are attached.
 */
export const registerSessionClosedBeacon = (apiBaseUrl, getSlug) => {
    const onUnload = () => notifySessionClosed(apiBaseUrl, getSlug(), 'tab_closed');
    const onVisibility = () => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            notifySessionClosed(apiBaseUrl, getSlug(), 'tab_hidden');
        }
    };
    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('pagehide', onUnload);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
        window.removeEventListener('beforeunload', onUnload);
        window.removeEventListener('pagehide', onUnload);
        document.removeEventListener('visibilitychange', onVisibility);
    };
};
